import { Tag } from "@faire/slate/components/tag";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { DATE_RANGE_OPTIONS } from "../constants/filters";
import { COPY } from "../copy";
import {
  formatCount,
  formatCurrency,
  formatPct,
  formatRoas,
  getInsightsTableRows,
  type InsightsTableRow,
} from "../insightsMockData";
import type { AdsDateRange, InsightsCustomerSegment } from "../types";

import {
  useCardPaddingClass,
  useIsCompact,
  useTableRowHeightClass,
} from "./CardDensity";
import { InsightsFilterMenu } from "./InsightsFilterMenu";
import { InsightsSectionTitle } from "./InsightsSectionTitle";
import { TablePagination } from "./TablePagination";

type InsightsTableSortColumn = keyof Pick<
  InsightsTableRow,
  | "storeType"
  | "adSpend"
  | "adSales"
  | "impressions"
  | "clicks"
  | "conversions"
  | "roas"
>;

type SortDirection = "asc" | "desc";

/** Rows per page when the caller doesn't size the card's neighbor itself. */
const DEFAULT_PAGE_SIZE = 5;

const SORTABLE_COLUMNS: {
  key: InsightsTableSortColumn;
  label: string;
}[] = [
  { key: "storeType", label: "Store type" },
  { key: "adSpend", label: "Ad spend" },
  { key: "adSales", label: "Total sales" },
  { key: "impressions", label: "Impressions" },
  { key: "clicks", label: "Clicks" },
  { key: "conversions", label: "Conversions" },
  { key: "roas", label: "Total sales / spend" },
];

/**
 * Column widths, so the layout doesn't shift when you page. Left to the
 * browser, a `table-auto` table sizes each column to the widest cell on the
 * page in front of it, so the columns jump every time the figures change
 * length. Keyed by column rather than by position, so V3's spend/sales swap
 * carries each width with its own data.
 */
const COLUMN_WIDTHS: Record<InsightsTableSortColumn, string> = {
  storeType: "17%",
  adSpend: "13%",
  adSales: "13%",
  // Single-word headers, so they can't wrap into a narrow column the way
  // "Total ad sales / spend" does — they need their full label width, and it
  // comes out of store type, whose values clip to an ellipsis.
  impressions: "16.5%",
  clicks: "10.5%",
  conversions: "16.5%",
  roas: "13.5%",
};

function sortableColumns(useTotalAdSales: boolean) {
  if (!useTotalAdSales) {
    return SORTABLE_COLUMNS;
  }
  const labeled = SORTABLE_COLUMNS.map((column) =>
    column.key === "adSales"
      ? { ...column, label: "Total ad sales" }
      : column.key === "roas"
        ? { ...column, label: "Total ad sales / spend" }
        : column
  );
  // V3 puts sales before spend so the money-in column leads the money-out
  // column, matching the Overview KPI order.
  const spendIndex = labeled.findIndex((column) => column.key === "adSpend");
  const salesIndex = labeled.findIndex((column) => column.key === "adSales");
  if (spendIndex < 0 || salesIndex < 0) {
    return labeled;
  }
  const next = [...labeled];
  [next[spendIndex], next[salesIndex]] = [next[salesIndex], next[spendIndex]];
  return next;
}

/**
 * V3 trims the vertical cell padding, because the spend and sales cells carry a
 * second percentage line: at `py-3` their content alone is 60px, so the row
 * overshot the 52px density row height no matter what the row class said.
 */
function useCellPaddingClass() {
  return useIsCompact() ? "px-3 py-2" : "px-3 py-3";
}

function AmountWithShare({ amount, share }: { amount: number; share: number }) {
  const cellPaddingClass = useCellPaddingClass();
  return (
    <td className={cellPaddingClass}>
      <div className="text-[#333]">{formatCurrency(amount)}</div>
      <div className="text-xs text-[#757575]">{formatPct(share)}</div>
    </td>
  );
}

function StoreTypeTableCell({
  column,
  row,
  preferredTag,
}: {
  column: InsightsTableSortColumn;
  row: InsightsTableRow;
  preferredTag: ReactNode;
}) {
  const cellPaddingClass = useCellPaddingClass();

  switch (column) {
    case "storeType":
      return (
        <td className={`max-w-[180px] text-[#333] ${cellPaddingClass}`}>
          <span className="flex items-center gap-2">
            {/* Long store-type names would otherwise widen this
                column and push the six figure columns out of the
                card; the full name stays available on hover. */}
            <span className="truncate" title={row.storeType}>
              {row.storeType}
            </span>
            {preferredTag}
          </span>
        </td>
      );
    case "adSpend":
      return <AmountWithShare amount={row.adSpend} share={row.adSpendPct} />;
    case "adSales":
      return <AmountWithShare amount={row.adSales} share={row.adSalesPct} />;
    case "impressions":
      return (
        <td className={`text-[#333] ${cellPaddingClass}`}>
          {formatCount(row.impressions)}
        </td>
      );
    case "clicks":
      return (
        <td className={`text-[#333] ${cellPaddingClass}`}>
          {formatCount(row.clicks)}
        </td>
      );
    case "conversions":
      return (
        <td className={`text-[#333] ${cellPaddingClass}`}>
          {formatCount(row.conversions)}
        </td>
      );
    case "roas":
      return (
        <td className={`text-[#333] ${cellPaddingClass}`}>
          {formatRoas(row.roas)}
        </td>
      );
  }
}

function SortIcon({ direction }: { direction: SortDirection }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      className="shrink-0 text-[#333]"
      aria-hidden
    >
      {direction === "asc" ? (
        <path d="M5 2L8 7H2L5 2Z" fill="currentColor" />
      ) : (
        <path d="M5 8L2 3H8L5 8Z" fill="currentColor" />
      )}
    </svg>
  );
}

function SortableHeader({
  label,
  column,
  activeColumn,
  direction,
  onSort,
}: {
  label: string;
  column: InsightsTableSortColumn;
  activeColumn: InsightsTableSortColumn;
  direction: SortDirection;
  onSort: (column: InsightsTableSortColumn) => void;
}) {
  const isActive = activeColumn === column;

  return (
    <th
      className="px-3 py-1.5 font-normal"
      aria-sort={
        isActive ? (direction === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 text-left"
        onClick={() => onSort(column)}
      >
        <span
          className={
            isActive
              ? "faire-label-medium text-[#333]"
              : "faire-label-medium text-[#333] hover:underline"
          }
        >
          {label}
        </span>
        {isActive ? <SortIcon direction={direction} /> : null}
      </button>
    </th>
  );
}

function compareRows(
  left: InsightsTableRow,
  right: InsightsTableRow,
  column: InsightsTableSortColumn,
  direction: SortDirection
): number {
  const multiplier = direction === "asc" ? 1 : -1;
  const primary =
    column === "storeType"
      ? left.storeType.localeCompare(right.storeType)
      : left[column] - right[column];

  if (primary !== 0) {
    return multiplier * primary;
  }

  // Store types tied on the sorted column are separated by impressions, in the
  // same direction, then by name — so a tie never leaves the order depending on
  // whichever sequence the rows happened to arrive in.
  if (column !== "impressions") {
    const byImpressions = left.impressions - right.impressions;
    if (byImpressions !== 0) {
      return multiplier * byImpressions;
    }
  }

  return left.storeType.localeCompare(right.storeType);
}

type StoreTypePerformanceTableProps = {
  hasBoosting: boolean;
  selectedStoreTypes: string[];
  customerSegment: InsightsCustomerSegment;
  /** V3 renames the sales columns to "Total ad sales" / "Total ad sales / spend". */
  useTotalAdSales?: boolean;
  onOpenStoreTypesModal: () => void;
  /** Control rendered opposite the title, e.g. the cumulative-view toggle. */
  headerAction?: ReactNode;
  /**
   * Rows per page. Set it from whatever decides the card's height so the table
   * fills its column instead of leaving the bottom of the card empty.
   */
  rowsPerPage?: number;
};

const TABLE_TITLE_TOOLTIP = COPY.customers.storeTypeTable.titleTooltip;

export function StoreTypePerformanceTable({
  hasBoosting,
  selectedStoreTypes,
  customerSegment,
  useTotalAdSales = false,
  onOpenStoreTypesModal,
  headerAction,
  rowsPerPage = DEFAULT_PAGE_SIZE,
}: StoreTypePerformanceTableProps) {
  const [sortColumn, setSortColumn] =
    useState<InsightsTableSortColumn>("clicks");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [page, setPage] = useState(1);
  const cardPaddingClass = useCardPaddingClass();
  const rowHeightClass = useTableRowHeightClass();
  const isCompact = useIsCompact();
  // This is the only cumulative surface on Customers. Keeping the date range
  // here avoids implying that it also truncates the monthly chart or the
  // paginated conversion list.
  const [dateRange, setDateRange] = useState<AdsDateRange>("12m");

  const rows = useMemo(
    () =>
      getInsightsTableRows(
        dateRange,
        customerSegment,
        selectedStoreTypes,
        hasBoosting
      ),
    [dateRange, customerSegment, selectedStoreTypes, hasBoosting]
  );

  const sortedRows = useMemo(() => {
    return [...rows].sort((left, right) =>
      compareRows(left, right, sortColumn, sortDirection)
    );
  }, [rows, sortColumn, sortDirection]);

  // Re-sorting or re-filtering changes which store types lead the list, so both
  // send you back to the first page.
  const handleSort = (column: InsightsTableSortColumn) => {
    setPage(1);
    if (sortColumn === column) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortColumn(column);
    setSortDirection(column === "storeType" ? "asc" : "desc");
  };

  // Either filter reranks the store types, which makes the page number refer to
  // a different set of rows.
  useEffect(() => {
    setPage(1);
  }, [customerSegment, dateRange]);

  const columns = sortableColumns(useTotalAdSales);
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
  const currentPage = Math.min(page, totalPages);
  const pageRows = sortedRows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <div
      className={`bg-fs-surface-primary flex h-full flex-col rounded-lg border border-[#dfe0e1] ${cardPaddingClass}`}
    >
      <div className="mb-4 flex flex-col gap-3 min-[733px]:flex-row min-[733px]:items-center min-[733px]:justify-between">
        {/* Distinguished from the chart beside it, which plots the same subject
            month by month. */}
        <InsightsSectionTitle tooltip={TABLE_TITLE_TOOLTIP}>
          {COPY.customers.storeTypeTable.title}
        </InsightsSectionTitle>
        {/* justify-end so the expand control still sits at the card's right edge
            below 733px, where this row stacks under the title. */}
        <div className="flex items-center justify-end gap-4">
          {hasBoosting ? (
            <button
              type="button"
              className="text-sm text-[#333] underline"
              onClick={onOpenStoreTypesModal}
            >
              Edit store types
            </button>
          ) : null}
          {headerAction}
        </div>
      </div>
      <div className="mb-4 flex">
        <InsightsFilterMenu
          name="customers-store-type-date-range"
          labelPrefix="Date range"
          menuTitle="Filter by date range"
          value={dateRange}
          options={DATE_RANGE_OPTIONS}
          onChange={setDateRange}
          applyToConfirm
        />
      </div>
      <div className="flex-1 overflow-x-auto">
        {/* Compact enough for all seven columns to remain visible beside the
            chart at the desktop split; it still scrolls on smaller cards. */}
        <table className="w-full min-w-[640px] table-fixed border-collapse text-left text-sm">
          <colgroup>
            {columns.map(({ key }) => (
              <col key={key} style={{ width: COLUMN_WIDTHS[key] }} />
            ))}
          </colgroup>
          {/* V3 drops the gray fill for a single rule under the labels, so the
              card reads like the conversions tables. */}
          <thead className={isCompact ? undefined : "bg-[#f7f7f7]"}>
            <tr className={isCompact ? "border-b border-[#dfe0e1]" : undefined}>
              {columns.map(({ key, label }) => (
                <SortableHeader
                  key={key}
                  label={label}
                  column={key}
                  activeColumn={sortColumn}
                  direction={sortDirection}
                  onSort={handleSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const isPreferred = selectedStoreTypes.includes(row.storeType);
              return (
                <tr
                  key={row.storeType}
                  className={`border-b border-[#dfe0e1] last:border-0 hover:bg-[#fafafa] ${rowHeightClass}`}
                >
                  {columns.map(({ key }) => (
                    <StoreTypeTableCell
                      key={key}
                      column={key}
                      row={row}
                      preferredTag={
                        hasBoosting && isPreferred ? (
                          <span className="shrink-0">
                            <Tag label="Preferred" variant="success" />
                          </span>
                        ) : null
                      }
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <div className="mt-4 flex justify-center">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onClick={setPage}
          />
        </div>
      ) : null}
    </div>
  );
}
